'use client';

import React, { useRef, useEffect, useState } from 'react';
import { Scan, Zap, AlertCircle, ChevronRight } from 'lucide-react';
import type { KitComponent } from '@/app/components/KittingWorkspace';

interface KittingScanBarProps {
  onScan: (barcode: string) => void;
  activeComponent: KitComponent | null;
}

export default function KittingScanBar({ onScan, activeComponent }: KittingScanBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Auto-focus on mount and after each scan
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onScan(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (inputValue.trim()) {
        onScan(inputValue.trim());
        setInputValue('');
      }
    }
  };

  return (
    <div className="border-t border-border bg-card px-6 py-4 slide-up">
      <div className="max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-4">
          {/* Context */}
          <div className="flex-1 min-w-0">
            {activeComponent ? (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Zap size={12} className="text-primary" />
                  <span>Expecting:</span>
                </div>
                <span className="text-xs font-semibold text-foreground">{activeComponent.itemName}</span>
                <ChevronRight size={10} className="text-muted-foreground" />
                <span className="text-xs font-mono-data text-muted-foreground">{activeComponent.itemCode}</span>
                <span className="text-2xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  Batch: {activeComponent.batchSuggested}
                </span>
                <span className="text-2xs text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                  Need {activeComponent.requiredQty - activeComponent.scannedQty} more
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-primary">
                <AlertCircle size={12} />
                <span className="font-semibold">All items scanned — ready to complete kit</span>
              </div>
            )}
          </div>

          {/* Scan input */}
          <form onSubmit={handleSubmit} className="flex items-center gap-3">
            <div className="relative">
              <Scan
                size={16}
                className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${
                  isFocused ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  // Re-focus after a short delay to keep scanner active
                  setTimeout(() => inputRef.current?.focus(), 100);
                }}
                placeholder="Scan barcode or type item code..."
                className={`w-72 lg:w-96 pl-9 pr-4 py-2.5 rounded-lg bg-input border text-sm text-foreground placeholder-muted-foreground outline-none transition-all duration-150 font-mono-data ${
                  isFocused
                    ? 'scan-input-glow border-primary' :'border-border hover:border-muted-foreground/50'
                }`}
                disabled={!activeComponent}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <button
              type="submit"
              disabled={!inputValue.trim() || !activeComponent}
              className="px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold transition-all duration-150 hover:brightness-110 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Scan
            </button>
          </form>

          {/* Keyboard shortcut hint */}
          <div className="hidden lg:flex items-center gap-1 text-2xs text-muted-foreground">
            <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono">Enter</kbd>
            <span>to confirm</span>
          </div>
        </div>
      </div>
    </div>
  );
}